import { isStudioDesktop } from '@repo/studio-desktop-bridge/runtime'
import * as React from 'react'
import { toast } from '@repo/studio-components-toast'
import { useAidaOverlayStore } from '../stores/overlay-store'
import { takePipOverlayRestore, useAidaPipStore } from '../stores/picture-in-picture-store'
import {
  copyDocumentStyles,
  mountPictureInPictureShell,
  readDocumentPictureInPicture,
} from '../utils/picture-in-picture'
import { bindPictureInPictureScroll } from '../utils/picture-in-picture-scroll'
import type { DocumentPictureInPicture } from '../utils/picture-in-picture'
import {
  aidaSessionMetadata,
  holdPictureInPictureSession,
  keepPipSessionAfterClose,
  mediaSessionFromNavigator,
  pictureInPictureWindowShown,
  shouldRequestPictureInPictureWindow,
} from '../utils/picture-in-picture-session'

const PIP_WIDTH = 420
const PIP_HEIGHT = 720

let opening = false
let wantsFloating = false
let releaseSession: (() => void) | null = null
let stopWatchingOpener: (() => void) | null = null

export function useAidaPopOut() {
  const active = useAidaPipStore(state => state.active)
  const supported = readOfferedPictureInPicture() !== null

  const popOut = React.useCallback(() => {
    const api = readOfferedPictureInPicture()
    if (!api || opening || useAidaPipStore.getState().active) {
      return
    }
    opening = true
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      void Notification.requestPermission()
    }
    armFloatingSession()
    void openWindow(api)
      .catch(() => {
        endFloatingSession()
        toast({ title: "Couldn't open the floating window.", description: null })
      })
      .finally(() => {
        opening = false
      })
  }, [])

  const bringBack = React.useCallback(() => {
    endFloatingSession()
    useAidaPipStore.getState().pipWindow?.close()
  }, [])

  return { supported, active, popOut, bringBack }
}

function armFloatingSession() {
  wantsFloating = true
  watchOpener()
  if (releaseSession || typeof navigator === 'undefined') {
    return
  }
  const session = mediaSessionFromNavigator(navigator.mediaSession)
  if (!session) {
    return
  }
  releaseSession = holdPictureInPictureSession(
    session,
    () => {
      void reopenFromMediaSession()
    },
    aidaSessionMetadata(),
  )
}

function endFloatingSession() {
  wantsFloating = false
  releaseSession?.()
  releaseSession = null
  stopWatchingOpener?.()
  stopWatchingOpener = null
}

function watchOpener() {
  if (stopWatchingOpener || typeof document === 'undefined') {
    return
  }
  const onVisibility = () => {
    if (document.visibilityState !== 'visible' || !wantsFloating) {
      return
    }
    const pipWindow = useAidaPipStore.getState().pipWindow
    if (pictureInPictureWindowShown(pipWindow)) {
      return
    }
    if (!pipWindow || pipWindow.closed) {
      endFloatingSession()
      releasePopOut()
      return
    }
    // The pop-out document can still be hidden for a frame while the browser
    // shows it again. Only treat it as gone if it is still hidden after paint.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (document.visibilityState !== 'visible' || !wantsFloating) {
          return
        }
        const current = useAidaPipStore.getState().pipWindow
        if (pictureInPictureWindowShown(current)) {
          return
        }
        endFloatingSession()
        releasePopOut()
        if (current && !current.closed) {
          current.close()
        }
      })
    })
  }
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('pageshow', onVisibility)
  stopWatchingOpener = () => {
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('pageshow', onVisibility)
  }
}

function readOfferedPictureInPicture(): DocumentPictureInPicture | null {
  if (isStudioDesktop() || typeof window === 'undefined') {
    return null
  }
  return readDocumentPictureInPicture(window)
}

async function reopenFromMediaSession() {
  const api = readOfferedPictureInPicture()
  const shown = pictureInPictureWindowShown(api?.window ?? useAidaPipStore.getState().pipWindow)
  if (
    !api ||
    opening ||
    !shouldRequestPictureInPictureWindow({ wantsFloating, windowShown: shown })
  ) {
    return
  }
  opening = true
  try {
    await openWindow(api)
  } catch {
    // The browser asked and then refused. The chat is still on the page.
  } finally {
    opening = false
  }
}

async function openWindow(api: DocumentPictureInPicture) {
  const pipWindow = await api.requestWindow({ width: PIP_WIDTH, height: PIP_HEIGHT })
  copyDocumentStyles(document, pipWindow.document)
  const container = mountPictureInPictureShell(pipWindow.document)
  pipWindow.document.title = 'Aida'
  const stopScroll = bindPictureInPictureScroll(pipWindow.document)
  useAidaPipStore.setState({ active: true, pipWindow, container, dragging: false })
  pipWindow.addEventListener('pagehide', () => {
    stopScroll()
    releasePopOut()
    if (
      !keepPipSessionAfterClose({
        opening,
        wantsFloating,
        openerHidden: document.visibilityState === 'hidden',
      })
    ) {
      endFloatingSession()
    }
  })
}

function releasePopOut() {
  const restoreOverlay = takePipOverlayRestore()
  useAidaPipStore.setState({
    active: false,
    dragging: false,
    pipWindow: null,
    container: null,
    chatSlot: null,
  })
  if (restoreOverlay && !useAidaOverlayStore.getState().open) {
    useAidaOverlayStore.setState({ open: true })
  }
}
